// src/utils/paymentPlans/graduatedFactors.ts

// Parsed factor tables from graduated_ratio_tables.ts
// Structure: GRADUATED_FACTORS[termYears][interestRatePercent][stepIndex]
// stepIndex corresponds to the 2-year period (0 = years 1-2, 1 = years 3-4, etc.)
const GRADUATED_FACTORS: Record<number, Record<number, number[]>> = {
  10: {
    3: [0.5541, 0.728, 0.959, 1.2614, 1.6611],
    5: [0.5657, 0.7448, 0.9796, 1.2898, 1.6971],
    7: [0.5779, 0.7605, 1.0016, 1.3177, 1.7346],
    9: [0.5921, 0.7626, 1.0199, 1.3633, 1.7115], // Note: Last value seems off compared to others, using as provided
  },
  25: {
    3: [
      0.601,
      0.6579,
      0.7212,
      0.7887,
      0.8646,
      0.9489,
      1.0396,
      1.1387,
      1.2484,
      1.3686,
      1.4993,
      1.6427,
      1.8009, // 13 steps for 25 years (last step is 1 year)
    ],
    5: [
      0.7133, 0.7578, 0.8125, 0.8724, 0.9357, 1.0041, 1.0777, 1.1564, 1.2419, 1.3326, 1.4284,
      1.5327, 1.6456,
    ],
    7: [
      0.8249, 0.8631, 0.9027, 0.9437, 0.9876, 1.0329, 1.0795, 1.1291, 1.18, 1.2338, 1.2904, 1.3498,
      1.412,
    ],
    9: [
      0.8937, 0.9211, 0.9485, 0.9771, 1.0069, 1.0379, 1.0689, 1.1011, 1.1344, 1.169, 1.2047, 1.2417,
      1.2786,
    ],
  },
};

const SUPPORTED_RATES = [3, 5, 7, 9]; // In percent

/**
 * Calculates the graduated payment factor based on term, interest rate, and current month.
 * Uses linear interpolation for interest rates between 3%, 5%, 7%, and 9%.
 * Clamps to the nearest supported rate factor if the interest rate is outside the 3-9% range.
 * Returns 1.0 if inputs are invalid or lookup fails.
 * @param termYears The loan term in years (10 or 25).
 * @param annualInterestRate The annual interest rate (as a decimal, e.g., 0.07 for 7%).
 * @param monthIndex The 0-based index of the current simulation month.
 * @returns The calculated graduated payment factor.
 */
export function calculateGraduatedFactor(
  termYears: 10 | 25,
  annualInterestRate: number,
  monthIndex: number
): number {
  if (annualInterestRate < 0 || monthIndex < 0) {
    console.warn('[Graduated Factor Calc] Invalid input: Rate or MonthIdx invalid', {
      termYears,
      annualInterestRate,
      monthIndex,
    });
    return 1.0;
  }

  const ratePercent = annualInterestRate * 100;
  const stepIndex = Math.floor(monthIndex / 24);

  const termTable = GRADUATED_FACTORS[termYears];

  const referenceRateFactors = termTable[SUPPORTED_RATES[0]];

  const maxStepIndex = referenceRateFactors.length - 1;
  const currentStepIndex = Math.min(stepIndex, maxStepIndex);

  const clampedRatePercent = Math.max(
    SUPPORTED_RATES[0],
    Math.min(ratePercent, SUPPORTED_RATES[SUPPORTED_RATES.length - 1])
  );

  let lowerRate = SUPPORTED_RATES[0];
  let upperRate = SUPPORTED_RATES[SUPPORTED_RATES.length - 1];
  for (let i = 0; i < SUPPORTED_RATES.length - 1; i++) {
    if (clampedRatePercent >= SUPPORTED_RATES[i] && clampedRatePercent <= SUPPORTED_RATES[i + 1]) {
      lowerRate = SUPPORTED_RATES[i];
      upperRate = SUPPORTED_RATES[i + 1];
      break;
    }
  }

  const lowerFactorArray = termTable[lowerRate];
  const upperFactorArray = termTable[upperRate];

  const lowerFactor = lowerFactorArray[currentStepIndex];
  const upperFactor = upperFactorArray[currentStepIndex];

  if (lowerRate === upperRate || !isFinite(lowerFactor) || !isFinite(upperFactor)) {
    return isFinite(lowerFactor) ? lowerFactor : 1.0;
  } else {
    const rateRange = upperRate - lowerRate;
    const factorRange = upperFactor - lowerFactor;

    if (rateRange === 0) {
      return lowerFactor;
    }

    const interpolationRatio = (clampedRatePercent - lowerRate) / rateRange;
    const interpolatedFactor = lowerFactor + interpolationRatio * factorRange;

    if (!isFinite(interpolatedFactor)) {
      console.warn(
        '[Graduated Factor Calc] Interpolation resulted in invalid number:',
        interpolatedFactor
      );
      return 1.0;
    }
    return interpolatedFactor;
  }
}
